/**
 * Footer rendered at the bottom of every app route via the root
 * layout. Mirrors the marketing site's footer chrome so cross-
 * domain navigation feels like one product, but the links are
 * app-flavoured: release notes, manage bookings, help.
 *
 * Kept dependency-free of session state — the same footer renders
 * on signed-out routes (signin, signup, /accept-invite) too.
 */
import { Link } from '@tanstack/react-router';

const MARKETING_URL = 'https://bookaburra.com';

export function AppFooter() {
  return (
    <footer className="bg-brand-ink text-brand-cream">
      <div className="mx-auto max-w-6xl px-6 py-10 sm:px-12">
        <div className="grid gap-8 sm:grid-cols-3">
          <div>
            <p className="text-sm font-semibold text-brand-cream">Bookaburra</p>
            <p className="mt-2 max-w-sm text-xs text-brand-cream/60">
              Smart booking for service providers. Made in Australia.
            </p>
          </div>

          <FooterColumn title="Product">
            <FooterLink to="/release-notes">Release notes</FooterLink>
            <FooterExternal href={`${MARKETING_URL}/features`}>
              Features
            </FooterExternal>
            <FooterExternal href={`${MARKETING_URL}/pricing`}>
              Pricing
            </FooterExternal>
          </FooterColumn>

          <FooterColumn title="Support">
            <FooterLink to="/contact">Contact support</FooterLink>
            <FooterExternal href={`${MARKETING_URL}/legal`}>Legal</FooterExternal>
          </FooterColumn>
        </div>

        <p className="mt-10 border-t border-brand-cream/10 pt-5 text-xs text-brand-cream/50">
          © {new Date().getFullYear()} Bookaburra.
        </p>
      </div>
    </footer>
  );
}

function FooterColumn({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <h3 className="text-xs font-semibold uppercase tracking-widest text-brand-cream/50">
        {title}
      </h3>
      <ul className="mt-3 space-y-2">{children}</ul>
    </div>
  );
}

function FooterLink({
  to,
  children,
}: {
  to: string;
  children: React.ReactNode;
}) {
  return (
    <li>
      <Link
        to={to}
        className="text-sm text-brand-cream/70 hover:text-brand-cream"
      >
        {children}
      </Link>
    </li>
  );
}

function FooterExternal({
  href,
  children,
}: {
  href: string;
  children: React.ReactNode;
}) {
  return (
    <li>
      <a
        href={href}
        className="text-sm text-brand-cream/70 hover:text-brand-cream"
      >
        {children}
      </a>
    </li>
  );
}
